'use client'

import { useMemo } from 'react'
import { useResponsive } from 'responsive/hooks/useResponsive/useResponsive'
import { cx } from 'utils/cx'

import { CardImage } from './components/CardImage/CardImage'
import { Selectable } from './components/Selectable/Selectable'

import styles from './Card.module.scss'

interface CardProps {
	readonly 'data-testid'?: string
	readonly imageSrc: string
	readonly children: React.ReactNode
	readonly imageAlt?: string
	readonly className?: string
	readonly noImageText?: string
	readonly isDisabled?: boolean
	readonly onCardClick?: () => void
	readonly buttonProps?: React.ComponentProps<'button'>
	readonly showSeparator?: boolean
	readonly articleImageId?: string
	readonly colorId?: string
	readonly checkboxConfig?: {
		isSelected?: boolean
		onSelect: (uniqueId: string) => void
	}
	readonly uniqueId?: string
}

export function Card({
	'data-testid': dataTestId,
	imageSrc,
	children,
	className,
	noImageText,
	imageAlt = '',
	isDisabled = false,
	onCardClick,
	buttonProps,
	showSeparator,
	articleImageId,
	colorId,
	checkboxConfig,
	uniqueId,
}: CardProps) {
	const { isSmall } = useResponsive()
	const isSelectable = !isDisabled && !!checkboxConfig && uniqueId

	const onCardClickAction =
		isSelectable && isSmall && checkboxConfig?.onSelect
			? () => checkboxConfig.onSelect(uniqueId)
			: onCardClick

	const buttonPropsUpdated =
		isSelectable && isSmall
			? {
					role: 'checkbox',
					'aria-checked': checkboxConfig.isSelected,
				}
			: buttonProps

	const isClickable = !!onCardClickAction

	const renderContent = useMemo(
		() => (
			<div
				className={cx(
					isClickable ? styles.clickableWrapper : styles.wrapper,
					isDisabled && styles.disabled,
					showSeparator && styles.separator,
					className
				)}
				{...(dataTestId && { 'data-testid': dataTestId })}
			>
				<div className={styles.image}>
					<CardImage
						src={imageSrc}
						alt={imageAlt}
						noImageText={noImageText}
						articleImageId={articleImageId}
						colorId={colorId}
					/>
				</div>
				<div className={styles.children}>
					<>
						{children}
						{isSelectable && (
							<Selectable uniqueId={uniqueId} checkboxConfig={checkboxConfig} />
						)}
					</>
				</div>
			</div>
		),
		[isClickable, children]
	)

	return isClickable ? (
		<button
			type='button'
			className={cx(
				styles.button,
				className,
				isDisabled && styles.disabled,
				showSeparator && styles.separator
			)}
			{...(!isDisabled && {
				onClick: onCardClickAction,
			})}
			{...buttonPropsUpdated}
		>
			{renderContent}
		</button>
	) : (
		renderContent
	)
}
